import fp from 'lodash/fp';
import queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { matchPath, Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { branch, compose, lifecycle, renderComponent, withProps } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes } from '../../constants';
import { isPendingUser } from '../../services';
import { selectedPlanSelector } from '../../state/modules/global';
import { industryActions } from '../../state/modules/industry';
import { paymentActions } from '../../state/modules/payment';
import { currentUserSelector, userActions } from '../../state/modules/user';
import { AcceptInvitation, AfsLicence, Billing, ChoosePlan, CpdCategorys, CpdRequirements, InvitationSignUp, Loading, NoMatch, SignUp, TeamInvite, Welcome } from '../pages';
import { NoMatchNav, OnboardingNav } from '../partials';

const unAuthenticatedRoutesFull = [
  routes.onboarding.choosePlan,
  routes.onboarding.signup,
  routes.onboarding.invitationSignUp,
  routes.onboarding.acceptInvitation,
  routes.onboarding.invalid
];

const unAuthenticatedRoutesStartWith = [
  routes.onboarding.choosePlan,
  routes.onboarding.signup,
];

const withNavBar = (Component) => (props) => (
  <div className='clearfix'>
    <OnboardingNav {...props} />
    <Component {...props} />
  </div>
);

const withSelectedPlan = (selectedPlan) => compose(
  withProps({
    selectedPlan
  }),
  withNavBar
);

/**
 * Onboarding layout component.
 */
const Onboarding = ({
  selectedPlan
}) => {
  return (
    <div className='clearfix content-body'>
      <Switch>
        <Route path={routes.onboarding.choosePlanIndustry(':industryIdentifier')} render={withNavBar(ChoosePlan)} />
        <Route path={routes.onboarding.signupIndustry(':industryIdentifier')} render={withSelectedPlan(selectedPlan)(SignUp)} />
        <Route path={routes.onboarding.billingIndustry(':industryIdentifier')} render={withSelectedPlan(selectedPlan)(Billing)} />
        <Route path={routes.onboarding.cpdRequirementsIndustry(':industryIdentifier')} render={withSelectedPlan(selectedPlan)(CpdRequirements)} />
        <Route path={routes.onboarding.choosePlan} component={withNavBar(ChoosePlan)} />
        <Route path={routes.onboarding.signup} render={withSelectedPlan(selectedPlan)(SignUp)} />
        <Route path={routes.onboarding.billing} render={withSelectedPlan(selectedPlan)(Billing)} />
        <Route path={routes.onboarding.welcome} render={withSelectedPlan(selectedPlan)(Welcome)} />
        <Route path={routes.onboarding.afsLicence(':industryIdentifier')} render={withSelectedPlan(selectedPlan)(AfsLicence)} />
        <Route path={routes.onboarding.teamInvite} render={withSelectedPlan(selectedPlan)(TeamInvite)} />
        <Route path={routes.onboarding.cpdCategorys} render={withSelectedPlan(selectedPlan)(CpdCategorys)} />
        <Route path={routes.onboarding.cpdRequirements} render={withSelectedPlan(selectedPlan)(CpdRequirements)} />
        <Route path={routes.onboarding.invitationSignUp} render={withNavBar(InvitationSignUp)} />
        <Route path={routes.onboarding.acceptInvitation} render={withNavBar(AcceptInvitation)} />
        <Route component={
          () => [
            <NoMatchNav key='404-nav' />,
            <NoMatch key='404-body' />
          ]
        } />
      </Switch>
    </div>
  );
}

const mapStateToProps = (state) => ({
  isAuthenticated: state.session.isAuthenticated,
  plans: state.payment.plans,
  selectedPlan: selectedPlanSelector(state),
  user: currentUserSelector(state),
  isFetchingUserData: state.user.isFetchingUserData,
  isFetchingPlans: state.payment.isFetching,
  isFetchingIndustries: state.industry.isFetchingIndustries,
  industries: state.industry.industriesWithCpdCategorys
});

const mapDispatchToProps = (dispatch) => ({
  getUser: bindActionCreators(userActions.user.getCurrent, dispatch),
  getPlans: bindActionCreators(paymentActions.payment.getPlans, dispatch),
  getIndustriesWithCpdCategorys: bindActionCreators(industryActions.industries.industriesWithCpdCategorys, dispatch)
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({
    match,
    location: {
      pathname
    }
  }) => ({
    requiresAuthentication: !fp.includes(pathname)(unAuthenticatedRoutesFull) && !fp.includes(true)(fp.map((path) => pathname.startsWith(path))(unAuthenticatedRoutesStartWith))
  })),
  branch(
    ({ requiresAuthentication }) => requiresAuthentication,
    compose(
      branch(
        ({ isAuthenticated }) => !isAuthenticated,
        renderComponent(() => (
          <Redirect to={routes.logout} />
        ))
      ),
      branch(
        ({
          location: {
            pathname
          },
          isFetchingUserData,
          user
        }) => (
            !matchPath(pathname, { path: routes.onboarding.billing }) &&
            !isFetchingUserData &&
            isPendingUser(user)
          ),
        renderComponent(() => (
          <Redirect to={routes.onboarding.billing} />
        ))
      )
    )
  ),
  lifecycle({
    componentDidMount() {
      const {
        getPlans,
        getUser,
        plans,
        getIndustriesWithCpdCategorys
      } = this.props;

      getUser();
      getIndustriesWithCpdCategorys();

      if (plans.length === 0) {
        getPlans();
      }
    }
  }),
  branch(
    ({ isFetchingUserData, isFetchingPlans, isFetchingIndustries, plans, industries }) => isFetchingUserData || isFetchingPlans || isFetchingIndustries || plans.length === 0 || !industries,
    renderComponent(Loading)
  ),
  withProps(({
    location: {
      search
    },
    plans,
    selectedPlan
  }) => ({
    // Use selected plan explicitly set over the query parameter first,
    // as the local stored selected plan could be left from last sign up action.
    selectedPlan: fp.find(
      ({ id }) => id === queryString.parse(search).chosenPlan
    )(plans) || selectedPlan
  }))
)(Onboarding);



// WEBPACK FOOTER //
// ./src/containers/layouts/onboarding.js